import { inject, Injectable } from '@angular/core';
import { Http, QueryHandlerFor } from '@skylab-angular-shared/core';
import { PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO } from '@skylab-shared/business';
import { HttpService, QueryHandler, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { AddressDTO, AggregatedEmailStatusCode, EmailDeliveryStatus, InventoryDocumentKind, SaleDocumentKind } from '@skylab-shared/dataModel';
import { Mutable } from '@skylab-shared/types';
import { Transform, Type } from 'class-transformer';

interface RelatedDocument {
    documentId: number;
    kind: SaleDocumentKind | InventoryDocumentKind;
    number: string;
}

export type InventoryGoodsIssueGridItem = InventoryGoodsIssueGridItemDTO;

class InventoryGoodsIssueGridItemDTO {
    public readonly client: string;
    @Type(() => AddressDTO) public readonly clientAddress?: AddressDTO;

    public readonly clientId: number;
    public readonly currencyId: number;
    public readonly documentNumber: string;
    public readonly favourite: boolean;
    public readonly id: number;
    public readonly version: string;
    public readonly externalId: string;

    public readonly clientEmails: string[];

    @Transform(transformDTODate, { toClassOnly: true }) public readonly issueDate: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly goodsIssueDate: Date;
    public readonly kind: InventoryDocumentKind;
    public readonly issuer: string;
    public readonly note: string | null;

    public readonly totalNetValue: number;
    public readonly totalGrossValue: number;
    public readonly totalBaseCurrencyNetValue: number;
    public readonly totalBaseCurrencyGrossValue: number;

    public readonly relatedCommercialDocument: RelatedDocument;
    public readonly relatedInventoryDocument: RelatedDocument;

    public aggregatedEmailStatusCode: AggregatedEmailStatusCode;
    @Type(() => EmailDeliveryStatus) public emailStatuses: EmailDeliveryStatus[];
}

export class InventoryGoodsIssueGridQuery extends PagedCollectionQuery<InventoryGoodsIssueGridItem> { }

@Injectable({ providedIn: 'root'})
@QueryHandlerFor(InventoryGoodsIssueGridQuery)
export class InventoryGoodsIssueGridQueryHandler implements QueryHandler<InventoryGoodsIssueGridQuery, PagedCollectionQueryResult<InventoryGoodsIssueGridItem>> {
    #url = TaggedUrl.join('inventory/goodsIssue', [Tags.Api]);
    #http = inject(Http) as HttpService;

    public async handleAsync(query: InventoryGoodsIssueGridQuery): Promise<PagedCollectionQueryResult<InventoryGoodsIssueGridItem>> {
        const { data } = await this.#http.get<Mutable<PagedCollectionQueryResult<InventoryGoodsIssueGridItem>>>(this.#url, {
            params: { ...query.params },
            dtoObject: new PagedCollectionQueryResultDTO<InventoryGoodsIssueGridItemDTO>(InventoryGoodsIssueGridItemDTO)
        });

        return data;
    }
}
